import { useQueryClient } from '@tanstack/react-query';
import { FOUNDATION } from 'config/features';
import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef } from 'react';
import { queryKeys as sharedQueryKeys } from '../../queryKeys';
import errorCodes from '../../util/errorCodes';
import { clearActiveRole } from '../api/localStorage';
import { refreshToken as refreshTokenApi } from '../api/loginApi';
import { deleteLocalStorageToken, getLocalStorageToken, setLocalStorageToken } from '../api/tokenStorage';

export const TIME_BEFORE_EXPIRY = 5 * 60 * 1000;

const REFRESH_QUERY_KEY = ['App/auth/refresh'];

const EMPTY_TOKEN = { token: null, refreshToken: null, expires: null };

const noop = () => {};

const AuthContext = createContext({
  token: null,
  invalidRoles: false,
  error: null,
  setToken: noop,
  setInvalidRoles: noop,
  clearAuth: noop,
});

const initState = () => ({
  ...(getLocalStorageToken() || EMPTY_TOKEN),
  invalidRoles: false,
  error: null,
});

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_TOKEN':
      return {
        token: action.token,
        refreshToken: action.refreshToken,
        expires: action.expires,
        invalidRoles: false,
        error: null,
      };
    case 'CLEAR_TOKEN':
      return { ...EMPTY_TOKEN, invalidRoles: false, error: null };
    case 'SET_INVALID_ROLES':
      return { ...EMPTY_TOKEN, invalidRoles: true, error: null };
    case 'REFRESH_FAILED':
      return { ...EMPTY_TOKEN, invalidRoles: false, error: action.error };
    default:
      return state;
  }
};

const millisUntil = date => new Date(date).getTime() - Date.now();

const persistToken = ({ token, refreshToken, expires }) => {
  const stored = getLocalStorageToken() || {};
  const unchanged = stored.token === token && stored.refreshToken === refreshToken && stored.expires === expires;
  if (!FOUNDATION && token && !unchanged) {
    setLocalStorageToken({ token, refreshToken, expires });
  }
  if (stored.token && !token) {
    deleteLocalStorageToken();
  }
};

/**
 * Holds the JWT token state and keeps it persisted and fresh.
 *
 * - The token is persisted to localStorage, except in FOUNDATION mode.
 * - Outside FOUNDATION mode the token is refreshed when it is within `TIME_BEFORE_EXPIRY`
 *   of expiring: on a timer, and when the tab becomes visible again (timers are throttled
 *   in background tabs). Concurrent refreshes share one request through React Query.
 * - A failed refresh clears the authentication state.
 *
 * Must be rendered inside a `QueryClientProvider`.
 */
export const AuthProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const [state, dispatch] = useReducer(reducer, undefined, initState);
  const stateRef = useRef(state);
  stateRef.current = state;
  const { token, refreshToken, expires } = state;

  const setToken = useCallback(
    (newToken, newRefreshToken, newExpires) =>
      dispatch({ type: 'SET_TOKEN', token: newToken, refreshToken: newRefreshToken, expires: newExpires }),
    []
  );

  const setInvalidRoles = useCallback(() => dispatch({ type: 'SET_INVALID_ROLES' }), []);

  const clearAuth = useCallback(() => {
    dispatch({ type: 'CLEAR_TOKEN' });
    clearActiveRole();
    queryClient.removeQueries({ queryKey: [sharedQueryKeys.USER] });
  }, [queryClient]);

  const refresh = useCallback(
    () =>
      queryClient.query({
        queryKey: REFRESH_QUERY_KEY,
        staleTime: 0,
        gcTime: 0,
        retry: false,
        queryFn: async () => {
          const used = stateRef.current.refreshToken;
          const isSuperseded = () => stateRef.current.refreshToken !== used;
          try {
            const {
              token: newToken,
              refreshToken: newRefreshToken,
              expires: newExpires,
            } = await refreshTokenApi({
              refreshToken: used,
            });
            if (isSuperseded()) return null;
            dispatch({ type: 'SET_TOKEN', token: newToken, refreshToken: newRefreshToken, expires: newExpires });
            return null;
          } catch {
            if (isSuperseded()) return null;
            const error = new Error(errorCodes.app.REFRESH_TOKEN_ERROR);
            clearAuth();
            dispatch({ type: 'REFRESH_FAILED', error: error.message });
            throw error;
          }
        },
      }),
    [queryClient, clearAuth]
  );

  const refreshIfStale = useCallback(() => {
    const current = stateRef.current;
    if (FOUNDATION || !current.refreshToken || !current.expires) return;
    if (millisUntil(current.expires) <= TIME_BEFORE_EXPIRY) {
      refresh().catch(() => {});
    }
  }, [refresh]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: persistence is keyed on the token fields only
  useEffect(() => {
    persistToken(stateRef.current);
  }, [token, refreshToken, expires]);

  useEffect(() => {
    if (FOUNDATION || !refreshToken || !expires) return;
    const delay = Math.max(0, millisUntil(expires) - TIME_BEFORE_EXPIRY);
    const timeout = setTimeout(refreshIfStale, delay);
    return () => clearTimeout(timeout);
  }, [refreshToken, expires, refreshIfStale]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') refreshIfStale();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [refreshIfStale]);

  const value = useMemo(
    () => ({ token, invalidRoles: state.invalidRoles, error: state.error, setToken, setInvalidRoles, clearAuth }),
    [token, state.invalidRoles, state.error, setToken, setInvalidRoles, clearAuth]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
